import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { addressKeys, lookupPostcode, type AddressLookup } from '@/api/address';
import { rulesFor } from '@/lib/addressRules';
import { usePlatform } from '@/providers/PlatformProvider';

/**
 * Verify a postcode against a real postcode authority — BUILD-PLAN §43.
 *
 * The server runs the same check when the order is placed, so this exists to
 * tell the customer while they are still looking at the field, and to offer
 * the real town instead of making them guess.
 *
 * Three outcomes are surfaced, not two. "We could not check" is reported as
 * silence rather than as approval or as an error: claiming a postcode is fine
 * when the service was down would be a lie, and claiming it is wrong would
 * block a real address (§86).
 */

export interface PostcodeStatus {
  /** A lookup is in flight; the field should not accuse anyone yet. */
  isChecking: boolean;
  /** Verified as not existing. The only state that may be shown as an error. */
  isWrong: boolean;
  /** Verified, and the customer's town does not match. Offer, do not block. */
  suggestedCity: string | null;
  /** Verified region, for filling a field the customer left empty. */
  region: string | null;
  regionCode: string | null;
  data: AddressLookup | undefined;
}

/** Wait for typing to stop before spending a request. */
function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

export function usePostcodeLookup(
  countryCode: string,
  postcode: string,
  city: string,
): PostcodeStatus {
  const { countries } = usePlatform();
  const rules = rulesFor(countryCode, countries);
  const trimmed = postcode.trim();

  const debouncedPostcode = useDebounced(trimmed, 600);

  /*
   * Only ask once the entry is the right shape. Sending every keystroke's
   * worth of half-typed codes would spend requests on input we already know
   * is incomplete, and would flash an error at someone mid-word.
   */
  const looksComplete =
    debouncedPostcode.length >= 4 &&
    (!rules.postcodePattern || rules.postcodePattern.test(debouncedPostcode));

  const { data, isFetching } = useQuery({
    queryKey: addressKeys.lookup(countryCode, debouncedPostcode),
    queryFn: () => lookupPostcode(countryCode, debouncedPostcode),
    enabled: Boolean(rules.postcodeLabel) && looksComplete,
    staleTime: 60 * 60 * 1000,
    retry: false,
  });

  /* Stale while the debounce catches up — say nothing rather than something wrong. */
  const isCurrent = debouncedPostcode === trimmed;

  if (!isCurrent || !data || !data.checked) {
    return {
      isChecking: isFetching || (looksComplete && !data),
      isWrong: false,
      suggestedCity: null,
      region: null,
      regionCode: null,
      data,
    };
  }

  const typedCity = city.trim().toLowerCase();

  /*
   * Lenient, matching the server: a postcode covers several valid place
   * names, and "London" is correct for a code whose district is "Kensington
   * and Chelsea". Only offer a correction when nothing plausible matches.
   */
  const matches =
    typedCity === '' ||
    data.cities.some((candidate) => {
      const known = candidate.trim().toLowerCase();

      return known === typedCity || known.includes(typedCity) || typedCity.includes(known);
    }) ||
    (data.region ?? '').trim().toLowerCase().includes(typedCity);

  return {
    isChecking: false,
    isWrong: !data.exists,
    suggestedCity: data.exists && !matches ? data.suggestedCity : null,
    region: data.region,
    regionCode: data.regionCode,
    data,
  };
}
